import { styled } from '@mui/material/styles';

export const PAGE_Z_INDEX = {
  EXTRACTED_FIELD_HIGHLIGHTS: 10,
  OCR_WORDS: 20,
  STATEMENT_GRID: 30,
  APPROVAL_STAMP: 40,
};

export const PageArea = styled('div', {
  label: 'PageArea',
  shouldForwardProp: (propName) => !['paid', 'paymentInProgress'].includes(propName),
})(({ theme, paid, paymentInProgress }) => ({
  position: 'relative',
  cursor: '-webkit-grab',
  margin: theme.spacing(0, 'auto'),
  overflow: 'hidden',
  border: `2px solid ${theme.palette.grey[200]}`,
  borderRadius: 8,

  ...(paid && {
    borderColor: theme.palette.secondary.main,
    boxShadow: `0 0 0 2px inset ${theme.palette.secondary.main}`,
  }),

  ...(paymentInProgress &&
    !paid && {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px inset ${theme.palette.primary.main}`,
    }),
}));

export const PageWrapper = styled('div', { label: 'PageWrapper' })(() => ({
  overflow: 'hidden',
  position: 'relative',
  display: 'block',
  // The PDF subtree must stay LTR in every locale.
  //
  // `calculatePageWidth()` is passed both to PageArea's inline width and to react-pdf's
  // <Page width>. PageArea is border-box with a 2px border, so its *content* box ends up
  // 4px narrower than the canvas react-pdf renders — the canvas always overflows by 4px.
  // The OCR polygon overlays are `inset: 0` (content box) and use physical inline `left`
  // values, so alignment depends entirely on which way that overflow spills:
  //   LTR -> canvas pins to the content-box left edge, spills right, origin matches the
  //          overlay origin, boxes wrap the values correctly;
  //   RTL -> canvas pins to the right edge, spills left, sitting 4px off the overlay and
  //          shifting every polygon 4px to the right of its text (DA-19888).
  //
  // `/* @noflip */` is required because stylis-plugin-rtl/cssjanus rewrites
  // `direction: ltr` -> `direction: rtl` in the RTL emotion cache, which would otherwise
  // turn this guard into the exact bug it is meant to prevent.
  // NOTE: the 4px canvas/content-box mismatch itself is still latent here; pinning the
  // direction only keeps it harmless. Feeding react-pdf the content width would remove it.
  direction: 'ltr!important /* @noflip */',
  width: '100%',

  '&:hover + .DocumentPage-controls': {
    opacity: 1,
  },

  '& .react-pdf__Page__canvas': {
    borderRadius: 8,
  },
}));

export const DocumentStatusBadgeAnchor = styled('div', {
  label: 'DocumentStatusBadgeAnchor',
  shouldForwardProp: (propName) => propName !== 'width',
})(({ width }) => ({
  width,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  margin: '0 auto',
  // center the tab horizontally on the page's top edge
  display: 'flex',
  justifyContent: 'center',
  // purely informational — let clicks/drags pass through to the page underneath
  pointerEvents: 'none',
  // transform is set inline in Page so the tab pans horizontally in sync with
  // the page (which is translated imperatively during drag)
  zIndex: 2,
}));
